import React, {useMemo, useState} from 'react';
import {Form, Input, InputNumber, Select} from 'antd';
import dayjs from 'dayjs';
import {internalApi} from "../../../common/axios";
import {DIALOG_KEY} from "../../../shared/utils/enum";
import {buildUrl} from "../../../shared/utils/net";
import {TrophyChallengeEntity} from "./TrophyChallengeEntity";
import {CancelButton, DeleteButton, SaveButton} from "../../../shared/components/buttons/CustomButtons";
import CustomDateTimePicker from "../../../shared/components/date/CustomDateTimePicker";
import DateStepper from "../../../shared/components/date/DateStepper";
import {useQueryClient} from "@tanstack/react-query";
import toast from "react-hot-toast";

const {Option} = Select;

const TrophyChallengeEditModal = ({record, handleClose, dialogRef, handleSetDialog, modalOptions}) => {
    const queryClient = useQueryClient();
    const groupRows = modalOptions?.groupRows || [];
    const missionGroupOptions = modalOptions?.missionGroupOptions || [];

    const [rows, setRows] = useState(
        record && record.trophyId ? new TrophyChallengeEntity(record) : new TrophyChallengeEntity({})
    );

    const selectedGroup = useMemo(
        () => groupRows.find(g => g.seq === rows.challengeGroupIdx),
        [groupRows, rows.challengeGroupIdx]
    );

    const handleSetRows = (key, value) => {
        setRows(prev => new TrophyChallengeEntity({...prev, [key]: value}));
    };

    const handleSave = (e) => {
        e.preventDefault();
        if (rows.isInvalidate()) return;
        return internalApi.post('trophy_challenge', {data: rows.toJson()}).then(res => {
            toast.success(res.data.message);
            queryClient.invalidateQueries({queryKey: ['trophyChallenge']});
            handleSetDialog([{key: DIALOG_KEY.OPEN, value: false}, {key: DIALOG_KEY.CONFIRM_TEXT, value: ''}]);
            handleClose(true);
        }).catch(err => {
            toast.error(err?.response?.data?.message || '저장 실패');
            console.error(err);
        });
    };

    const handleDelete = (e) => {
        e.preventDefault();
        const currentDialog = dialogRef.current;
        if (currentDialog.confirmText === 'DELETE') {
            return internalApi.delete(buildUrl(`trophy_challenge/${rows.trophyId}`)).then(res => {
                toast.success(res.data.message);
                queryClient.invalidateQueries({queryKey: ['trophyChallenge']});
                handleSetDialog([{key: DIALOG_KEY.OPEN, value: false}, {key: DIALOG_KEY.CONFIRM_TEXT, value: ''}]);
                handleClose(true);
            }).catch(err => {
                toast.error('삭제 실패');
                console.error(err);
            });
        } else {
            toast.error("잘못된 확인 문자입니다. DELETE 를 입력하세요.");
            handleSetDialog([{key: DIALOG_KEY.CONFIRM_TEXT, value: ''}]);
        }
    };

    return (
        <div style={{marginTop: 26}}>
            <Form layout="vertical">
                <div style={{display: 'flex', gap: 16, flexWrap: 'wrap'}}>
                    <Form.Item label="메모" style={{flex: 1, minWidth: 200}}>
                        <Input
                            value={rows.memo}
                            onChange={e => handleSetRows('memo', e.target.value)}
                        />
                    </Form.Item>
                    <Form.Item label="활성/비활성" style={{width: 110}}>
                        <Select value={rows.onOff} onChange={v => handleSetRows('onOff', v)}>
                            <Option value={1}>On</Option>
                            <Option value={0}>Off</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item label="계정 레벨" style={{width: 110}}>
                        <InputNumber
                            min={1}
                            value={rows.openLevel}
                            onChange={v => handleSetRows('openLevel', v)}
                            style={{width: '100%'}}
                        />
                    </Form.Item>
                </div>

                <div style={{display: 'flex', gap: 16, flexWrap: 'wrap'}}>
                    <Form.Item label="챌린지 그룹" required style={{width: 240}}>
                        <Select
                            value={rows.challengeGroupIdx || undefined}
                            onChange={v => handleSetRows('challengeGroupIdx', v)}
                            placeholder="그룹 선택"
                            showSearch
                            optionFilterProp="children"
                        >
                            {groupRows.map(g => (
                                <Option key={g.seq} value={g.seq}>
                                    {g.seq}{g.memo ? `(${g.memo})` : ''}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item label="챌린지 미션" required style={{width: 240}}>
                        <Select
                            value={rows.missionGroupIdx || undefined}
                            onChange={v => handleSetRows('missionGroupIdx', v)}
                            placeholder="미션 그룹 선택"
                            showSearch
                            optionFilterProp="children"
                        >
                            {missionGroupOptions.map(m => (
                                <Option key={m.groupIdx} value={m.groupIdx}>
                                    {m.groupIdx}{m.memo ? `(${m.memo})` : ''}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item label="완료 조건(트로피 개수)" required style={{width: 160}}>
                        <InputNumber
                            min={1}
                            value={rows.clearCount}
                            onChange={v => handleSetRows('clearCount', v)}
                            style={{width: '100%'}}
                        />
                    </Form.Item>
                    <Form.Item label="최소 적용 버전" style={{width: 140}}>
                        <Input
                            value={selectedGroup?.minVersion || '0.0.0'}
                            disabled
                        />
                    </Form.Item>
                </div>

                <Form.Item label="기간">
                    <CustomDateTimePicker rows={rows} handleSetRows={handleSetRows} hasAdd={false}/>
                </Form.Item>
                <DateStepper setRows={setRows} entity={TrophyChallengeEntity}/>

                <div style={{marginTop: 16}}>
                    <SaveButton onClick={handleSave} handleSetDialog={handleSetDialog}/>
                    <CancelButton onClick={handleClose}/>
                    <DeleteButton
                        onClick={handleDelete}
                        handleSetDialog={handleSetDialog}
                        display={rows.trophyId > 0 ? 'flex' : 'none'}
                    />
                </div>
            </Form>
        </div>
    );
};

export default TrophyChallengeEditModal;
